import { Vector3 } from "three";

import { FabrikChain, Group, KabschGroup, OrphanGroup } from "./group";
import { Edge } from "./edge";
import { Joint } from "./joint";
import { GeneralConstraint } from "./constraint/general";
import { ConstraintMetadataEntry, PointMetadata, PointType } from "./metadata";

import { JointPositions } from "../types";
import { DummyGroup } from "./group/dummy";
import { GroupState } from "./group/group";
import { PositionDetails } from "../misc/position";
import { SkeletalPosition } from "../types/statsApi";

const deltaV = new Vector3();

export class Armature {
  root: Joint;
  groups: Group[] = [];

  pseudoRoot: Joint;
  fixedRoot: boolean;

  rootKabsch?: KabschGroup;

  nullGroup: DummyGroup;

  joints: Map<string, Joint> = new Map();

  private maxError = 0.5;

  private posedJoints = new JointPositions();
  private posedDetails = new PositionDetails(["_waist", "ROOT"]);

  constructor(
    rootEdge: Edge,
    constraintData: ConstraintMetadataEntry[] = [],
    fixedRoot = true,
    rootKabsch?: KabschGroup
  ) {
    this.root = rootEdge.start;

    // NB: this pseudoRoot is overridden if client supplies pseudoRootName and
    //     is found
    this.pseudoRoot = rootEdge.end;
    this.fixedRoot = fixedRoot;
    this.rootKabsch = rootKabsch;

    this.parseConstraints(constraintData);

    const joints: Joint[] = [];
    this.root.getJoints(joints);
    joints.forEach((j) => this.joints.set(j.name, j));

    this.nullGroup = new DummyGroup(this.root);
  }

  pose(
    targets: JointPositions,
    maxIters = 40,
    position?: SkeletalPosition,
    apply = true
  ): boolean {
    this.solveGroups(targets, maxIters);

    const error = this.calcError(targets);
    if (position && error < this.maxError) {
      if (position.posed) {
        // TODO: do we want to allow this or error?
        console.warn(`reposing ${position.positionId}`);
      } else {
        position.posed = { state: new GroupState(), centroid: new Vector3() };
      }

      position.posed.state.clear();
      position.posed.state.error = error;
      this.nullGroup.save(position.posed.state);

      this.posedJoints.clear();
      this.root.getPoints(this.posedJoints);
      this.posedDetails.update(this.posedJoints);
      position.posed.centroid.copy(this.posedDetails.centroid.position);
    }

    if (apply) {
      this.apply();
    }

    // for (const group of this.getChains()) {
    //   group.cacheError();
    // }

    return true;
  }

  /**
   * Just detects shoulder pop for now
   * TODO: entirely rework error detection
   * @param targets
   */
  calcError(targets: JointPositions) {
    const rTarget = targets.get("RShoulder");
    const lTarget = targets.get("LShoulder");
    const neckTarget = targets.get("Neck");
    const rSolution = this.joints.get("RShoulder")?.pos;
    const lSolution = this.joints.get("LShoulder")?.pos;
    const neckSolution = this.joints.get("Neck")?.pos;

    if (
      !rTarget ||
      !lTarget ||
      !neckTarget ||
      !rSolution ||
      !lSolution ||
      !neckSolution
    ) {
      console.error(
        "unable to calculate error",
        rTarget,
        lTarget,
        neckTarget,
        rSolution,
        lSolution,
        neckSolution
      );
      return 0;
    }

    return (
      rTarget.distanceToSquared(rSolution) +
      lTarget.distanceToSquared(lSolution) +
      neckTarget.distanceToSquared(neckSolution)
    );
  }

  load(state: GroupState) {
    this.nullGroup.load(state);
  }

  interpolate(a: GroupState, b: GroupState, alpha: number) {
    const blended = GroupState.clone(a);

    blended.blend(b, alpha);
    this.nullGroup.load(blended);

    this.apply();
  }

  moveBy(delta: Vector3) {
    for (const childEdge of this.root.childEdges) {
      childEdge.moveBones(delta);
    }
  }

  private apply() {
    for (const childEdge of this.root.childEdges) {
      childEdge.updateBones();
    }
  }

  private solveGroups(targets: JointPositions, maxIters = 40) {
    // move root by moving common point pseudoRoot
    if (!this.fixedRoot) {
      if (this.rootKabsch) {
        this.rootKabsch.solveForward(targets);
      } else {
        const pseudoRootTarget = targets.get(this.pseudoRoot.name);
        if (!pseudoRootTarget) {
          console.warn(`point data has no pseudo root ${this.pseudoRoot.name}`);
        } else {
          deltaV.subVectors(pseudoRootTarget, this.pseudoRoot.pos);
          this.root.pos.add(deltaV);
        }
      }
    }

    let iter = 0;
    for (; iter < maxIters; iter++) {
      // FORWARD
      for (let i = 0; i < this.groups.length; i++) {
        const group = this.groups[i];

        if (!group.solveForward(targets)) {
          console.warn("failure to run fabrikForward");
        }
      }

      // BACKWARD
      for (let i = this.groups.length - 1; i >= 0; i--) {
        const group = this.groups[i];

        if (!group.solveBackward(targets)) {
          console.warn("failure to run fabrikBackward");
        }

        // TODO: reintroduce annealing when we can get a smooth anneal from
        //       previous solution
        // TODO: work out why there are so many states
        // TODO: only anneal the highest chain in branch
        // if ((group instanceof FabrikChain) && (group.annealable))
        // {
        //   const states: GroupState[] = [];
        //   const errors: number[] = [];
        //   // TODO: handle caching of solutions so we can seek to frameNum
        //   let [error, delta] = group.calcError();
        //   group.snapshot(states);
        //   errors.push(error);

        //   if (!group.converged(error) && group.diverging(delta))
        //   {
        //     console.log("ANNEAL");
        //     group.anneal(targets);

        //     [error, delta] = group.calcError();
        //     group.snapshot(states);
        //     errors.push(error);

        //     let cnt = 0;
        //     while (cnt++ < 20)
        //     {
        //       if (!group.solveBackward(targets))
        //       {
        //         console.warn("failure to re-solve after annealing")
        //       }

        //       [error, delta] = group.calcError();
        //       group.snapshot(states);
        //       errors.push(error);
        //     }

        //     group.calcError();
        //     if (!group.converged(error))
        //     {
        //       group.rotateTowardsTarget(targets);
        //       [error, delta] = group.calcError();
        //       group.snapshot(states);
        //       errors.push(error);
        //     }

        //     let bestError: number | undefined;
        //     let bestState: GroupState | undefined;
        //     let bestIndex: number = 0;
        //     for (let index = 0; index < states.length; index++)
        //     {
        //       const state  = states[index];
        //       const sError = errors[index];
        //       if ((bestError == undefined) || (sError < bestError))
        //       {
        //         bestError = sError;
        //         bestState = state;
        //         bestIndex = index;
        //       }
        //     }

        //     console.log("states.length", states.length);

        //     // TODO: this doesn't seem to be improving things - fix it
        //     if ((bestError != group.error) && bestState)
        //     {
        //       console.log("loading", bestIndex);
        //       group.load(bestState)
        //     }
        //   }

        //   [error, delta] = group.calcError();
        // }
      }

      // check for convergence of fabrik chains
      // TODO: tighten up global convergence check
      let converged = true;
      for (const chain of this.getChains()) {
        const [error, _] = chain.calcError();
        if (!chain.converged(error)) {
          // console.log(`${chain.effector.name} fabrik chain not converged: ${error}`);
          converged = false;
          // break;
        }
      }

      if (converged) {
        break;
      }
    }
  }

  getEdge(name: string): Edge | undefined {
    const toVisit = [...this.root.childEdges];
    let current: Edge | undefined = undefined;
    let found: Edge | undefined = undefined;

    while ((current = toVisit.pop())) {
      if (current.name == name) {
        found = current;
        break;
      }

      toVisit.push(...current.end.childEdges);
    }

    return found;
  }

  getJoint(name: string): Joint | undefined {
    return this.root.getChildJoint(name);
  }

  getChains(): FabrikChain[] {
    const out: FabrikChain[] = [];

    this.groups.forEach((g) => {
      if (g instanceof FabrikChain) {
        out.push(g);
      }
    });

    return out;
  }

  getChain(name: string): FabrikChain | undefined {
    for (const group of this.groups) {
      if (group instanceof FabrikChain && group.effector.name == name) {
        return group;
      }
    }

    return undefined;
  }

  parseGroups(pointData: PointMetadata, pseudoRootName?: string) {
    this.labelJoints(pointData, pseudoRootName);
    // determine groupings from metadata
    const toVisit = [this.root];
    let current: Joint | undefined = undefined;

    const delimiters = [];

    while ((current = toVisit.pop())) {
      // seek to end of each branch and work backwards
      if (current.childEdges.length != 0) {
        for (const childEdge of current.childEdges) {
          toVisit.push(childEdge.end);
        }
      } else {
        // backtrack upwards and find group delimiters
        while (current) {
          if (
            current.isKabschParent() ||
            current.isHub() ||
            current.isLeafEffector() ||
            current.props.type == PointType.KABSCH ||
            current.isOrphaned()
          ) {
            // TODO: remove delimiter logs - useful for debugging group parsing
            // const isA = [
            //   current.isKabschParent(), current.isKabschChild(), current.isHub(),
            //   current.isLeafEffector(), (current.props.type == PointType.KABSCH), current.isOrphaned()
            // ];
            // const isAText = ["KABSCH PARENT", "KABSCH CHILD", "HUB", "LEAF", "KABSCH", "ORPHAN"].filter((_, i) => isA[i]);

            // console.log(`${current.name} is delimiter`, isAText);

            // keep chainLeaves in bottom-up order
            // TODO: is there a better datastructure for this?
            const index = delimiters.indexOf(current);
            if (index > -1) {
              delimiters.splice(index, 1);
            }
            delimiters.push(current);
          }

          current = current.parentEdge?.start;
        }
      }
    }

    const joints: Joint[] = [];
    this.root.getJoints(joints);

    for (const joint of delimiters) {
      let group: Group | undefined;
      if (joint.isKabschParent() || joint.isHub() || joint.isLeafEffector()) {
        group = new FabrikChain(joint, this.fixedRoot);
        this.groups.push(group);
        // console.log(`group ${joint.name} - FABRIK`, group.joints.map(j => j.name));
      } else if (joint.props.type == PointType.KABSCH) {
        const children = joints.filter((j) =>
          joint.props.children?.includes(j.name)
        );
        group = new KabschGroup(
          joint,
          joint.props.children ? children : undefined
        );
        this.groups.push(group);
        // console.log(`group ${joint.name} - KABSCH`, group.joints.map(j => j.name));
      } else if (joint.isOrphaned() && joint.parentEdge) {
        group = new OrphanGroup(joint.parentEdge.start);
        this.groups.push(group);
        // console.log(`group ${joint.name} - ORPHAN`, group.joints.map(j => j.name));
      }

      if (!group) {
        console.error(`invalid group delimiter ${joint.name}`);
      }
    }

    // console.log("GROUPS", this.groups);
    // console.log("group names", this.groups.map((g) => [g.constructor.name, "-", ...g.joints.map((j) => j.name)]));
  }

  labelJoints(pointData: PointMetadata, pseudoRootName?: string) {
    // label joints
    const joints: Joint[] = [];
    this.root.getJoints(joints);
    for (const joint of joints) {
      // set pseudoRoot if we find it
      if (pseudoRootName && joint.name == pseudoRootName) {
        this.pseudoRoot = joint;
        // console.log(`setting pseudoRoot to ${joint.name}`);
      }

      if (pointData.hasOwnProperty(joint.name)) {
        joint.props = pointData[joint.name];
      } else if (joint.props.type === null) {
        joint.props.type = PointType.FLOATING;
      }

      if (joint.props.type == PointType.KABSCH) {
        for (const edge of joint.childEdges) {
          const child = edge.end;
          if (
            pointData.hasOwnProperty(child.name) &&
            pointData[child.name].type != PointType.TARGETED
          ) {
            console.error(
              `children of kabsch (${joint.name}) should not be ` +
                `labelled anything but targeted, relabeling ${child.name}`
            );
          }
          child.props.type = PointType.TARGETED;
        }
      }
    }
  }

  private parseConstraints(data: ConstraintMetadataEntry[]) {
    let count = 0;
    for (const cdata of data) {
      const parent = this.getEdge(cdata.parent);
      const child = this.getEdge(cdata.child);

      if (!parent || !child) {
        console.warn(
          `unable to find ${cdata.parent} or ${cdata.child} in ` +
            `model (constraint #${count})`
        );
        count++;
        continue;
      }

      const constraint = new GeneralConstraint(
        cdata.twistLims,
        cdata.xLims,
        cdata.zLims,
        cdata.relative
      );

      if (!parent.addConstraint(constraint, child)) {
        console.error(
          `unable to add constraint ${cdata.parent} -> ${cdata.child}` +
            ` (#${count})`
        );
      }
      count++;
    }
  }
}
